import React, { useMemo } from 'react';
import { Card, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../common/Flex';
import Avatar from '../../common/Avatar';

const TopClients = ({ orders, assignments, clients }) => {
  const topClientsData = useMemo(() => {
    const clientStats = new Map();

    // Procesar órdenes por cliente
    orders.forEach(order => {
      const clientId = order.clientId;
      if (!clientStats.has(clientId)) {
        const client = clients.find(c => c.ClientID === clientId);
        clientStats.set(clientId, {
          id: clientId,
          name: client ? client.ClientName : `Cliente ${clientId}`,
          totalInvestment: 0,
          totalAssigned: 0,
          ordersCount: 0,
          activeOrders: 0
        });
      }

      const stats = clientStats.get(clientId);
      stats.totalInvestment += parseFloat(order.netInvestment) || 0;
      stats.ordersCount += 1;

      // Verificar si la orden está activa
      const now = new Date();
      const startDate = new Date(order.dateFrom);
      const endDate = new Date(order.dateTo);
      if (startDate <= now && endDate >= now) {
        stats.activeOrders += 1;
      }
    });

    // Agregar asignaciones
    assignments.forEach(assignment => {
      const order = orders.find(o => o.id === assignment.insertionOrderId);
      if (!order) return;

      const clientId = order.clientId;
      if (clientStats.has(clientId)) {
        let amount = parseFloat(assignment.montoValor) || 0;
        
        if (assignment.amountType === 'percentage') {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
        
        clientStats.get(clientId).totalAssigned += amount;
      }
    });

    // Convertir a array y calcular métricas adicionales
    return Array.from(clientStats.values())
      .map(client => ({
        ...client,
        utilization: client.totalInvestment > 0 ? (client.totalAssigned / client.totalInvestment) * 100 : 0,
        avgOrderValue: client.ordersCount > 0 ? client.totalInvestment / client.ordersCount : 0
      }))
      .sort((a, b) => b.totalInvestment - a.totalInvestment)
      .slice(0, 8); // Top 8 clientes
  }, [orders, assignments, clients]);

  const getUtilizationColor = (utilization) => {
    if (utilization > 100) return 'danger';
    if (utilization > 90) return 'warning';
    if (utilization > 70) return 'success';
    return 'info';
  };

  const getClientInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <Card className="h-100">
      <Card.Header>
        <Flex justifyContent="between" alignItems="center">
          <h6 className="mb-0">Top Clientes</h6>
          <FontAwesomeIcon icon="crown" className="text-warning" />
        </Flex>
      </Card.Header>
      <Card.Body className="p-0">
        <ListGroup variant="flush">
          {topClientsData.map((client, index) => (
            <ListGroup.Item key={client.id} className="px-3 py-3">
              <Flex alignItems="center">
                <div className="me-3">
                  <Avatar
                    name={getClientInitials(client.name)}
                    size="l"
                    className={`bg-${getUtilizationColor(client.utilization)}-subtle text-${getUtilizationColor(client.utilization)}`}
                  />
                </div>
                <div className="flex-1">
                  <h6 className="mb-1 fs-10 fw-semibold">
                    {client.name}
                    {index < 3 && (
                      <Badge 
                        bg={index === 0 ? 'warning' : index === 1 ? 'secondary' : 'success'} 
                        className="ms-2 fs-11"
                      >
                        #{index + 1}
                      </Badge>
                    )}
                  </h6>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-0 fs-11 text-500">
                        <FontAwesomeIcon icon="dollar-sign" className="me-1" />
                        ${(client.totalInvestment / 1000).toFixed(1)}K
                      </p>
                      <p className="mb-0 fs-11 text-500">
                        <FontAwesomeIcon icon="chart-bar" className="me-1" />
                        {client.ordersCount} órdenes
                      </p>
                    </div>
                    <div className="text-end">
                      <Badge 
                        bg={getUtilizationColor(client.utilization)}
                        className="mb-1"
                      >
                        {client.utilization.toFixed(0)}%
                      </Badge>
                      <p className="mb-0 fs-11 text-500">
                        {client.activeOrders} activas
                      </p>
                    </div>
                  </div>
                </div>
              </Flex>
              
              {/* Barra de progreso de utilización */}
              <div className="mt-2">
                <div className="progress" style={{ height: '4px' }}>
                  <div
                    className={`progress-bar bg-${getUtilizationColor(client.utilization)}`}
                    role="progressbar"
                    style={{ width: `${Math.min(100, client.utilization)}%` }}
                    aria-valuenow={client.utilization}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
        
        {topClientsData.length === 0 && (
          <div className="text-center py-4">
            <FontAwesomeIcon icon="users" className="text-muted fs-2 mb-2" />
            <p className="text-muted mb-0">No hay datos de clientes disponibles</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TopClients; 